import { useState, useRef, useCallback, type DragEvent } from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { Shell } from '../../components/layout/Shell';
import { Button } from '../../components/ui/Button';
import { Input, Textarea, Select } from '../../components/ui/Input';
import { ComboBox } from '../../components/ui/ComboBox';
import { SelectWithOther } from '../../components/ui/SelectWithOther';
import { useToast } from '../../components/ui/Toast';
import { useCreateInventoryItem, useUploadItemImage, useAnalyzeImage } from '../../hooks/useInventory';
import { useTaxonomySuggestions } from '../../hooks/useTaxonomy';
import { useAppMode } from '../../context/AppModeContext';
import { usePlatformLabel } from '../../hooks/usePlatformLabel';
import { Route as rootRoute } from '../__root';
import { useTranslation } from '../../i18n';
import {
  CATEGORIES,
  CONDITIONS,
  CURRENCIES,
  VIDEO_SYSTEMS,
  CATEGORY_PLATFORMS,
  PLATFORMS,
} from '@siliconvault/shared';
import type { CreateInventoryItemInput } from '@siliconvault/shared';
import styles from './batch.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/batch',
  component: BatchUploadPage,
});

// ---------- types ----------

interface AnalysisResult {
  name: string;
  category: string;
  platform: string;
  subcategory: string | null;
  condition: string;
  videoSystem: string | null;
  description: string;
  confidence: 'high' | 'medium' | 'low';
  cpu?: string | null;
  memory?: string | null;
  launchDate?: string | null;
  launchPrice?: string | null;
  launchPriceCurrency?: string | null;
  launchPriceInflationAdjusted?: string | null;
}

type BatchItemStatus = 'queued' | 'analyzing' | 'ready' | 'skipped' | 'error' | 'saving' | 'saved';

interface BatchItem {
  id: string;
  file: File;
  previewUrl: string;
  status: BatchItemStatus;
  error?: string;
  analysis?: AnalysisResult;
  formData: Partial<CreateInventoryItemInput>;
  collapsed: boolean;
}

// ---------- helpers ----------

const MAX_PX = 1024;
const JPEG_QUALITY = 0.85;
const MAX_CONCURRENT = 3;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function resizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let { width, height } = img;
      if (width <= MAX_PX && height <= MAX_PX && file.size <= 3 * 1024 * 1024) {
        return resolve(file);
      }
      if (width > MAX_PX || height > MAX_PX) {
        const scale = MAX_PX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Compression failed'));
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

let nextId = 0;
function uid() {
  return `batch-${Date.now()}-${++nextId}`;
}

// ---------- Sub-components ----------

function BatchItemCard({
  item,
  onUpdate,
  onRemove,
  onRetry,
}: {
  item: BatchItem;
  onUpdate: (id: string, formData: Partial<CreateInventoryItemInput>) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const { t } = useTranslation();
  const platformLabel = usePlatformLabel();
  const [collapsed, setCollapsed] = useState(item.collapsed);
  const fd = item.formData;

  const category = fd.category ?? 'Computer';
  const platformOptions = CATEGORY_PLATFORMS[category] ?? [...PLATFORMS];
  const { suggestions: subcategorySuggestions } = useTaxonomySuggestions('subcategory', category, fd.platform ?? '');

  function set(patch: Partial<CreateInventoryItemInput>) {
    onUpdate(item.id, { ...fd, ...patch });
  }

  function handleCategoryChange(newCategory: string) {
    if ((CATEGORIES as readonly string[]).includes(newCategory)) {
      const platforms = CATEGORY_PLATFORMS[newCategory] ?? [...PLATFORMS];
      set({ category: newCategory, platform: platforms[0] ?? '', subcategory: null });
    } else {
      set({ category: newCategory });
    }
  }

  const statusBadge = () => {
    switch (item.status) {
      case 'queued':
        return <span className={styles.badge}>{t('batch.queued')}</span>;
      case 'analyzing':
        return <span className={`${styles.badge} ${styles.badgeAnalyzing}`}>{t('batch.analyzing')}</span>;
      case 'ready':
        return item.analysis?.confidence === 'low'
          ? <span className={`${styles.badge} ${styles.badgeWarning}`}>{t('batch.lowConfidence')}</span>
          : <span className={`${styles.badge} ${styles.badgeReady}`}>{t('batch.ready')}</span>;
      case 'skipped':
        return <span className={`${styles.badge} ${styles.badgeWarning}`}>{t('batch.skipped')}</span>;
      case 'error':
        return <span className={`${styles.badge} ${styles.badgeError}`}>{t('batch.error')}</span>;
      case 'saving':
        return <span className={`${styles.badge} ${styles.badgeAnalyzing}`}>{t('batch.saving')}</span>;
      case 'saved':
        return <span className={`${styles.badge} ${styles.badgeSaved}`}>{t('batch.saved')}</span>;
      default:
        return null;
    }
  };

  return (
    <div className={`${styles.card} ${item.status === 'saved' ? styles.cardSaved : ''}`}>
      <div className={styles.cardHeader} onClick={() => setCollapsed(!collapsed)}>
        <img src={item.previewUrl} alt="" className={styles.cardThumb} />
        <div className={styles.cardHeaderInfo}>
          <div className={styles.cardName}>{fd.name || item.file.name}</div>
          <div className={styles.cardMeta}>
            {fd.category && <span>{fd.category}</span>}
            {fd.platform && <span> · {fd.platform}</span>}
          </div>
        </div>
        {statusBadge()}
        <button
          className={styles.cardToggle}
          onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? '▸' : '▾'}
        </button>
      </div>

      {!collapsed && (
        <div className={styles.cardBody}>
          {item.status === 'analyzing' && (
            <div className={styles.analyzeOverlay}>
              <div className={styles.spinner} />
              {t('aiScan.analyzing')}
            </div>
          )}

          {item.status === 'error' && (
            <div className={styles.errorBar}>
              <span>{item.error || t('aiScan.failed')}</span>
              <Button type="button" variant="secondary" onClick={() => onRetry(item.id)}>
                {t('batch.retry')}
              </Button>
            </div>
          )}

          {(item.status === 'ready' || item.status === 'skipped' || item.status === 'saving' || item.status === 'saved') && (
            <div className={styles.cardForm}>
              <Input
                label={t('itemForm.name')}
                value={fd.name ?? ''}
                onChange={(e) => set({ name: e.target.value })}
                placeholder={t('itemForm.namePlaceholder')}
                required
                disabled={item.status === 'saving' || item.status === 'saved'}
              />

              <div className={styles.formRow}>
                <SelectWithOther
                  label={t('itemForm.category')}
                  value={category}
                  onChange={handleCategoryChange}
                  options={CATEGORIES}
                  placeholder={t('itemForm.otherPlaceholder')}
                  required
                />
                <SelectWithOther
                  label={platformLabel}
                  value={fd.platform ?? ''}
                  onChange={(v) => set({ platform: v })}
                  options={platformOptions}
                  placeholder={t('itemForm.otherPlaceholder')}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <ComboBox
                  label={t('itemForm.subcategory')}
                  value={(fd as any).subcategory ?? ''}
                  onChange={(v) => set({ subcategory: v || null } as any)}
                  suggestions={subcategorySuggestions}
                  placeholder={t('itemForm.subcategoryPlaceholder')}
                />
                <Select
                  label={t('itemForm.videoSystem')}
                  value={(fd as any).videoSystem ?? ''}
                  onChange={(e) => set({ videoSystem: e.target.value || null } as any)}
                >
                  <option value="">—</option>
                  {VIDEO_SYSTEMS.map((vs) => (
                    <option key={vs} value={vs}>
                      {t(`videoSystem.${vs}` as any)}
                    </option>
                  ))}
                </Select>
              </div>

              <div className={styles.formRow}>
                <Select
                  label={t('itemForm.condition')}
                  value={fd.condition ?? 'good'}
                  onChange={(e) => set({ condition: e.target.value as any })}
                >
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {t(`condition.${c}` as any)}
                    </option>
                  ))}
                </Select>
                <Input
                  label={t('itemForm.quantity')}
                  type="number"
                  min="0"
                  value={String(fd.quantity ?? 1)}
                  onChange={(e) => set({ quantity: parseInt(e.target.value, 10) || 1 })}
                />
              </div>

              <Textarea
                label={t('itemForm.description')}
                value={fd.description ?? ''}
                onChange={(e) => set({ description: e.target.value })}
                placeholder={t('itemForm.descriptionPlaceholder')}
                rows={3}
              />

              <Input
                label={t('itemForm.location')}
                value={(fd as any).location ?? ''}
                onChange={(e) => set({ location: e.target.value } as any)}
                placeholder={t('itemForm.locationPlaceholder')}
              />

              {(category === 'Computer' || category === 'Console') && (
                <div className={styles.formRow}>
                  <Input
                    label={t('itemForm.cpu')}
                    value={(fd.metadata as any)?.cpu ?? ''}
                    onChange={(e) => set({ metadata: { ...((fd.metadata as any) ?? {}), cpu: e.target.value } })}
                    placeholder={t('itemForm.cpuPlaceholder')}
                    disabled={item.status === 'saving' || item.status === 'saved'}
                  />
                  <Input
                    label={t('itemForm.memory')}
                    value={(fd.metadata as any)?.memory ?? ''}
                    onChange={(e) => set({ metadata: { ...((fd.metadata as any) ?? {}), memory: e.target.value } })}
                    placeholder={t('itemForm.memoryPlaceholder')}
                    disabled={item.status === 'saving' || item.status === 'saved'}
                  />
                </div>
              )}

              <div className={styles.formRow}>
                <Input
                  label={t('itemForm.launchDate')}
                  value={(fd.metadata as any)?.launchDate ?? ''}
                  onChange={(e) => set({ metadata: { ...((fd.metadata as any) ?? {}), launchDate: e.target.value } })}
                  placeholder={t('itemForm.launchDatePlaceholder')}
                  disabled={item.status === 'saving' || item.status === 'saved'}
                />
                <Input
                  label={t('itemForm.launchPrice')}
                  value={(fd.metadata as any)?.launchPrice ?? ''}
                  onChange={(e) => set({ metadata: { ...((fd.metadata as any) ?? {}), launchPrice: e.target.value } })}
                  placeholder="0.00"
                  disabled={item.status === 'saving' || item.status === 'saved'}
                />
                <Select
                  label={t('itemForm.launchPriceCurrency')}
                  value={(fd.metadata as any)?.launchPriceCurrency ?? 'USD'}
                  onChange={(e) => set({ metadata: { ...((fd.metadata as any) ?? {}), launchPriceCurrency: e.target.value } })}
                  disabled={item.status === 'saving' || item.status === 'saved'}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </div>

              {(fd.metadata as any)?.launchPriceInflationAdjusted && (
                <Input
                  label={t('itemForm.inflationAdjustedPrice')}
                  value={`$${(fd.metadata as any).launchPriceInflationAdjusted}`}
                  onChange={() => {}}
                  readOnly
                  disabled
                />
              )}

              <Textarea
                label={t('itemForm.customNotes')}
                value={(fd.metadata as any)?.customNotes ?? ''}
                onChange={(e) => set({ metadata: { ...((fd.metadata as any) ?? {}), customNotes: e.target.value } })}
                placeholder={t('itemForm.customNotesPlaceholder')}
                rows={2}
                disabled={item.status === 'saving' || item.status === 'saved'}
              />

              <div className={styles.formRow}>
                <Input
                  label={t('itemForm.acquisitionDate')}
                  type="date"
                  value={fd.acquisitionDate ?? ''}
                  onChange={(e) => set({ acquisitionDate: e.target.value })}
                />
                <div className={styles.priceGroup}>
                  <Input
                    label={t('itemForm.acquisitionPrice')}
                    value={fd.acquisitionPrice ?? ''}
                    onChange={(e) => set({ acquisitionPrice: e.target.value })}
                    placeholder="0.00"
                  />
                  <Select
                    label={t('itemForm.currency')}
                    value={fd.currency ?? 'USD'}
                    onChange={(e) => set({ currency: e.target.value as any })}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          )}

          {item.status !== 'saved' && item.status !== 'saving' && (
            <div className={styles.cardActions}>
              <Button type="button" variant="secondary" onClick={() => onRemove(item.id)}>
                {t('batch.remove')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------- Main page ----------

function BatchUploadPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { hasAiAccess } = useAppMode();
  const analyzeMutation = useAnalyzeImage();
  const createMutation = useCreateInventoryItem();
  const uploadImageMutation = useUploadItemImage();

  const [items, setItems] = useState<BatchItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);

  // --- file handling ---

  const [quotaExhausted, setQuotaExhausted] = useState(false);

  const makeSkippedItem = useCallback((it: BatchItem): BatchItem => ({
    ...it,
    status: 'skipped' as const,
    formData: {
      name: it.file.name.replace(/\.\w+$/, ''),
      category: 'Computer',
      platform: '',
      condition: 'good',
      quantity: 1,
      currency: 'USD',
      description: '',
      acquisitionDate: null,
      acquisitionPrice: null,
      location: null,
      metadata: {},
    },
    collapsed: false,
  }), []);

  const processQueue = useCallback(async (current: BatchItem[]) => {
    if (processingRef.current) return;
    processingRef.current = true;

    // Find items still queued
    const queued = current.filter((i) => i.status === 'queued');
    if (queued.length === 0) {
      processingRef.current = false;
      return;
    }

    let hitQuotaLimit = false;

    // Process in chunks of MAX_CONCURRENT
    for (let i = 0; i < queued.length; i += MAX_CONCURRENT) {
      // If quota was hit in a previous chunk, skip remaining as manual-entry
      if (hitQuotaLimit) {
        const remaining = queued.slice(i);
        setItems((prev) => prev.map((it) =>
          remaining.some((r) => r.id === it.id) ? makeSkippedItem(it) : it,
        ));
        break;
      }

      const chunk = queued.slice(i, i + MAX_CONCURRENT);

      // Mark as analyzing
      setItems((prev) => prev.map((it) =>
        chunk.some((c) => c.id === it.id) ? { ...it, status: 'analyzing' as const } : it,
      ));

      const results = await Promise.allSettled(
        chunk.map(async (item) => {
          const result = await analyzeMutation.mutateAsync(item.file);
          return { id: item.id, result };
        }),
      );

      // Detect quota errors (429 status)
      const hasQuotaError = results.some(
        (r) => r.status === 'rejected' && (r.reason as any)?.statusCode === 429,
      );
      if (hasQuotaError) {
        hitQuotaLimit = true;
        setQuotaExhausted(true);
      }

      setItems((prev) => prev.map((it) => {
        const res = results.find((r) => {
          if (r.status === 'fulfilled') return r.value.id === it.id;
          return false;
        });

        if (res && res.status === 'fulfilled') {
          const analysis = res.value.result;
          const metadata: Record<string, unknown> = {};
          if (analysis.cpu) metadata.cpu = analysis.cpu;
          if (analysis.memory) metadata.memory = analysis.memory;
          if (analysis.launchDate) metadata.launchDate = analysis.launchDate;
          if (analysis.launchPrice) metadata.launchPrice = analysis.launchPrice;
          if (analysis.launchPriceCurrency) metadata.launchPriceCurrency = analysis.launchPriceCurrency;
          if (analysis.launchPriceInflationAdjusted) metadata.launchPriceInflationAdjusted = analysis.launchPriceInflationAdjusted;
          return {
            ...it,
            status: 'ready' as const,
            analysis,
            formData: {
              name: analysis.name,
              category: analysis.category,
              platform: analysis.platform,
              condition: analysis.condition as CreateInventoryItemInput['condition'],
              videoSystem: analysis.videoSystem as CreateInventoryItemInput['videoSystem'],
              subcategory: analysis.subcategory,
              description: analysis.description,
              quantity: 1,
              currency: 'USD' as const,
              acquisitionDate: null,
              acquisitionPrice: null,
              location: null,
              metadata,
            },
            collapsed: false,
          };
        }

        // Check if this item errored due to quota
        const errIdx = chunk.findIndex((c) => c.id === it.id);
        if (errIdx >= 0 && results[errIdx]?.status === 'rejected') {
          const reason = (results[errIdx] as PromiseRejectedResult).reason as any;
          if (reason?.statusCode === 429) {
            // Quota hit — skip gracefully for manual entry
            return makeSkippedItem(it);
          }
          return { ...it, status: 'error' as const, error: t('aiScan.failed') };
        }

        return it;
      }));
    }

    processingRef.current = false;
  }, [analyzeMutation, t, makeSkippedItem]);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const newItems: BatchItem[] = [];

    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) continue;
      try {
        const resized = await resizeImage(file);
        const previewUrl = URL.createObjectURL(resized);
        newItems.push({
          id: uid(),
          file: resized,
          previewUrl,
          status: hasAiAccess ? 'queued' : 'ready',
          formData: hasAiAccess ? {} : {
            name: file.name.replace(/\.\w+$/, ''),
            category: 'Computer',
            platform: 'Commodore',
            condition: 'good',
            quantity: 1,
            currency: 'USD',
            description: '',
            acquisitionDate: null,
            acquisitionPrice: null,
            location: null,
            metadata: {},
          },
          collapsed: true,
        });
      } catch {
        // skip bad files
      }
    }

    if (newItems.length === 0) return;

    setItems((prev) => {
      const updated = [...prev, ...newItems];
      if (hasAiAccess) {
        // Kick off processing after state update
        setTimeout(() => processQueue(updated), 0);
      }
      return updated;
    });
  }, [hasAiAccess, processQueue]);

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  }

  // --- item operations ---

  function updateItem(id: string, formData: Partial<CreateInventoryItemInput>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, formData } : it)));
  }

  function removeItem(id: string) {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  }

  function retryItem(id: string) {
    setItems((prev) => {
      const updated = prev.map((it) => (it.id === id ? { ...it, status: 'queued' as const, error: undefined } : it));
      setTimeout(() => processQueue(updated), 0);
      return updated;
    });
  }

  // --- batch save ---

  const readyCount = items.filter((i) => i.status === 'ready' || i.status === 'skipped').length;
  const savedCount = items.filter((i) => i.status === 'saved').length;
  const savingCount = items.filter((i) => i.status === 'saving').length;
  const errorCount = items.filter((i) => i.status === 'error').length;
  const analyzingCount = items.filter((i) => i.status === 'analyzing' || i.status === 'queued').length;
  const isSaving = savingCount > 0;
  const [saveTotal, setSaveTotal] = useState(0); // total items in current save batch

  async function handleSaveAll() {
    const toSave = items.filter((i) => i.status === 'ready' || i.status === 'skipped');
    if (toSave.length === 0) return;

    // Validate all have names
    const invalid = toSave.filter((i) => !i.formData.name?.trim());
    if (invalid.length > 0) {
      toast(t('batch.nameRequired'), 'error');
      // Expand the first invalid item
      setItems((prev) => prev.map((it) =>
        invalid[0].id === it.id ? { ...it, collapsed: false } : it,
      ));
      return;
    }

    // Mark all as saving
    setSaveTotal(toSave.length);
    setItems((prev) => prev.map((it) =>
      toSave.some((s) => s.id === it.id) ? { ...it, status: 'saving' as const } : it,
    ));

    let successCount = 0;

    for (const item of toSave) {
      try {
        const rawMeta = (item.formData.metadata ?? {}) as Record<string, unknown>;
        const metadata: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(rawMeta)) {
          if (typeof v === 'string' ? v.trim() : v) metadata[k] = typeof v === 'string' ? v.trim() : v;
        }
        const data: CreateInventoryItemInput = {
          name: (item.formData.name ?? '').trim(),
          category: item.formData.category ?? 'Computer',
          platform: item.formData.platform ?? 'Commodore',
          condition: (item.formData.condition ?? 'good') as typeof CONDITIONS[number],
          quantity: item.formData.quantity ?? 1,
          description: item.formData.description ?? '',
          acquisitionDate: item.formData.acquisitionDate || null,
          acquisitionPrice: item.formData.acquisitionPrice || null,
          currency: (item.formData.currency ?? 'USD') as typeof CURRENCIES[number],
          videoSystem: (item.formData as any).videoSystem || null,
          subcategory: (item.formData as any).subcategory || null,
          location: (item.formData as any).location || null,
          metadata,
        };

        const created = await createMutation.mutateAsync(data);

        // Upload the image
        try {
          await uploadImageMutation.mutateAsync({ itemId: created.id, file: item.file });
        } catch {
          // non-blocking
        }

        setItems((prev) => prev.map((it) =>
          it.id === item.id ? { ...it, status: 'saved' as const } : it,
        ));
        successCount++;
      } catch {
        setItems((prev) => prev.map((it) =>
          it.id === item.id ? { ...it, status: 'error' as const, error: t('batch.saveFailed') } : it,
        ));
      }
    }

    if (successCount > 0) {
      toast(t('batch.savedCount', { count: successCount }), 'success');
    }
  }

  function handleClearSaved() {
    setItems((prev) => {
      prev.filter((i) => i.status === 'saved').forEach((i) => URL.revokeObjectURL(i.previewUrl));
      return prev.filter((i) => i.status !== 'saved');
    });
  }

  return (
    <Shell>
      <div className={styles.page}>
        <Link to="/inventory" className={styles.backLink}>
          &larr; {t('itemDetail.backToInventory')}
        </Link>
        <h1 className={styles.title}>{t('batch.title')}</h1>
        <p className={styles.subtitle}>{t('batch.subtitle')}</p>

        {/* Drop zone — always visible for adding more */}
        <div
          className={`${styles.dropZone} ${dragging ? styles.dropZoneDragging : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className={styles.fileInput}
            onChange={handleInputChange}
          />
          <div className={styles.dropIcon}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M12 8v8M8 12h8" />
            </svg>
          </div>
          <div className={styles.dropText}>
            {items.length === 0 ? t('batch.dropText') : t('batch.dropMore')}
          </div>
          <div className={styles.dropHint}>{t('batch.dropHint')}</div>
        </div>

        {/* Status bar */}
        {items.length > 0 && (
          <div className={styles.statusBar}>
            <div className={styles.statusLeft}>
              <span>{t('batch.total', { count: items.length })}</span>
              {analyzingCount > 0 && (
                <span className={styles.statusAnalyzing}>
                  <span className={styles.miniSpinner} />
                  {t('batch.analyzingCount', { count: analyzingCount })}
                </span>
              )}
              {readyCount > 0 && (
                <span className={styles.statusReady}>{t('batch.readyCount', { count: readyCount })}</span>
              )}
              {savedCount > 0 && (
                <span className={styles.statusSaved}>{t('batch.savedCountLabel', { count: savedCount })}</span>
              )}
            </div>
            <div className={styles.statusRight}>
              {savedCount > 0 && (
                <Button type="button" variant="secondary" onClick={handleClearSaved}>
                  {t('batch.clearSaved')}
                </Button>
              )}
              {readyCount > 0 && (
                <Button
                  type="button"
                  onClick={handleSaveAll}
                  loading={isSaving}
                  disabled={isSaving || readyCount === 0}
                >
                  {t('batch.createAll', { count: readyCount })}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Quota exhaustion banner */}
        {quotaExhausted && (
          <div className={styles.quotaBanner}>
            <div className={styles.quotaBannerText}>
              {t('batch.quotaExhausted')}
            </div>
            <Link to="/upgrade" className={styles.quotaBannerCta}>
              {t('batch.quotaUpgrade')}
            </Link>
          </div>
        )}

        {/* Progress banner — visible during and after saving */}
        {saveTotal > 0 && (isSaving || savedCount > 0) && (
          <div className={`${styles.progressBanner} ${!isSaving && savedCount > 0 ? styles.progressBannerDone : ''}`}>
            {isSaving ? (
              <>
                <div className={styles.progressInfo}>
                  <span className={styles.miniSpinner} />
                  <span>{t('batch.savingProgress', { done: savedCount + errorCount, total: saveTotal })}</span>
                </div>
                <div className={styles.progressTrack}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${((savedCount + errorCount) / saveTotal) * 100}%` }}
                  />
                </div>
                <div className={styles.progressHint}>{t('batch.savingHint')}</div>
              </>
            ) : (
              <>
                <div className={styles.progressInfo}>
                  <span className={styles.progressCheck}>&#10003;</span>
                  <span>
                    {errorCount > 0
                      ? t('batch.doneWithErrors', { saved: savedCount, errors: errorCount })
                      : t('batch.allDone', { count: savedCount })}
                  </span>
                </div>
                <Link to="/inventory" className={styles.progressLink}>
                  {t('batch.viewInventory')} &rarr;
                </Link>
              </>
            )}
          </div>
        )}

        {/* Item cards */}
        <div className={styles.cardList}>
          {items.map((item) => (
            <BatchItemCard
              key={item.id}
              item={item}
              onUpdate={updateItem}
              onRemove={removeItem}
              onRetry={retryItem}
            />
          ))}
        </div>

        {/* Bottom actions when there are many items */}
        {items.length > 3 && readyCount > 0 && (
          <div className={styles.bottomActions}>
            <Button
              type="button"
              onClick={handleSaveAll}
              loading={isSaving}
              disabled={isSaving || readyCount === 0}
            >
              {t('batch.createAll', { count: readyCount })}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate({ to: '/inventory' })}>
              {t('itemForm.cancel')}
            </Button>
          </div>
        )}
      </div>
    </Shell>
  );
}
